import React, { FC, useEffect, useMemo } from 'react'
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import { TextField as MuiTextField } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { RulesConfigurationFormType } from '../../types/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faPlane, faStar } from '@fortawesome/free-solid-svg-icons';
import { ServiceType } from 'shared/models/ServiceTypes';

interface GrouperAndServiceTypeStepProps {
    servicesOptions: ServiceType[];
}
const GrouperAndServiceTypeStep: FC<GrouperAndServiceTypeStepProps> = ({ servicesOptions }) => {
    const { values, setFieldValue, errors, touched } = useFormikContext<RulesConfigurationFormType>();

    const selectedServiceNames = useMemo(() => {
        return servicesOptions
            .filter((option) => values.service_type_ids.includes(option.id))
            .map((option) => option.name);
    }, [servicesOptions, values.service_type_ids]);

    const computedTitle = useMemo(() => {
        const modeParts: string[] = [];

        if (values.is_moment) modeParts.push('Para el Momento');
        if (values.is_airport) modeParts.push('Aeropuerto');
        if (values.is_priority) modeParts.push('Prioritario');

        const mode = modeParts.join(' - ');
        const servicesPart = selectedServiceNames.length ? `(${selectedServiceNames.join(', ')})` : '';

        return [mode, servicesPart].filter(Boolean).join(' - ');
    }, [selectedServiceNames, values.is_airport, values.is_moment, values.is_priority]);

    useEffect(() => {
        // Mantener el campo requerido listo para el submit sin que el usuario tenga que escribirlo.
        if (values.name !== computedTitle) {
            setFieldValue('name', computedTitle);
        }
    }, [computedTitle, setFieldValue]);

    return (
        <div className="flex flex-col w-full gap-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="space-y-4">
                <MuiTextField
                    fullWidth
                    name="name"
                    label="Titulo"
                    variant="outlined"
                    value={computedTitle}
                    className="bg-bgInputSection"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                />
            </div>
            <div className="flex items-center gap-4">
                <div className="w-2 h-8 bg-secondary-500 rounded-full" />
                <Typography variant="h6" className="font-bold text-secondary-700 ">Agrupadores</Typography>
            </div>
            <div className="flex gap-2 items-center bg-bgInputSection rounded-xl border border-borderInputSection p-2">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.is_moment}
                            onChange={(e) => {
                                setFieldValue('is_moment', e.target.checked)
                                if (e.target.checked) {
                                    setFieldValue('service_type_ids', [ ...servicesOptions.filter((option) => option.id !== 5).map((option) => option.id as never) ])
                                    setFieldValue('is_airport', false)
                                    setFieldValue('is_priority', false)
                                } else {
                                    setFieldValue('service_type_ids', [])
                                }
                            }}
                            color="primary"
                        />
                    }
                    label="¿Es para el momento?"
                />
                <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="flex gap-2 items-center bg-bgInputSection rounded-xl border border-borderInputSection p-2">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.is_airport}
                            onChange={(e) => {
                                setFieldValue('is_airport', e.target.checked)
                                if (e.target.checked) {
                                    setFieldValue('is_moment', false)
                                    setFieldValue('is_priority', true)
                                    setFieldValue('service_type_ids', [ ...servicesOptions.filter((option) => option.id !== 5).map((option) => option.id as never) ])
                                }else if( !values.is_priority ){
                                    setFieldValue('service_type_ids', [])
                                }

                            }}
                            color="primary"
                        />
                    }
                    label="¿Es para el aeropuerto?"
                />
                <FontAwesomeIcon icon={faPlane} />
            </div>
            <div className="flex gap-2 items-center bg-bgInputSection rounded-xl border border-borderInputSection p-2">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.is_priority}
                            onChange={(e) => {
                                setFieldValue('is_priority', e.target.checked);
                                if (e.target.checked) {
                                    setFieldValue('is_moment', false)
                                    setFieldValue('service_type_ids', [ ...servicesOptions.filter((option) => option.id !== 5).map((option) => option.id as never) ])
                                }else if( !values.is_airport ){
                                    setFieldValue('service_type_ids', [])
                                }
                            }}
                            color="primary"
                        />
                    }
                    label="¿Es prioritario?"
                />
                <FontAwesomeIcon icon={faStar} />
            </div>
            <div className="flex items-center gap-4">
                <div className="w-2 h-8 bg-secondary-500 rounded-full" />
                <Typography variant="h6" className="font-bold text-secondary-700">Tipos de servicio</Typography>
            </div>
            <div className="w-full space-y-4">
                <div className="flex gap-3 items-center p-3 rounded-lg bg-bgInputSection border border-borderInputSection">
                    {servicesOptions.map((option) => (
                        <FormControlLabel
                            key={option.id}
                            control={
                                <Checkbox
                                    checked={values.service_type_ids.includes(option.id)}
                                    color="primary"
                                    size="small"
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const nextValue = isChecked
                                            ? [...values.service_type_ids, option.id]
                                            : values.service_type_ids.filter(v => v !== option.id);
                                        setFieldValue('service_type_ids', nextValue);
                                    }}
                                />
                            }
                            label={<span className="text-sm text-gray-700 font-medium">{option.name}</span>}
                        />
                    ))}
                </div>
                {errors.service_type_ids && touched.service_type_ids && (
                    <span className="text-red-500 text-xs">{errors.service_type_ids}</span>
                )}
            </div>
        </div>
    )
}

export default GrouperAndServiceTypeStep